import { CSSProperties, JSX, MouseEvent, ReactNode, RefObject } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { BaseCrud, IDataProviderFilter } from 'core-components/types'

import { ITHeadProps } from '../table/models/ITHead'

export interface ICellAttrs {
  key: string
  className?: string
  style?: CSSProperties
}

export interface ICellAttrsStorage {
  [key: string]: ICellAttrs
}

export interface RowState {
  selected: boolean
  dragged: boolean
  dragOvered: boolean
  dragBefore: boolean
  dragAfter: boolean
  expanded: boolean
  loading: boolean
  indeterminate: boolean
  hasChildren: boolean
  cellAttrs: ICellAttrsStorage
  focusOn: boolean // TODO: check it
}

export type TplFunction<T> = (
  item: T,
  renderChildren: (
    item: T,
    rowState: RowState,
    additional: any,
    focusedId?: any,
    rowAttrs?: any
  ) => JSX.Element,
  rowState: RowState,
  rowAttrs: any,
  additional: any,
  focusedId: any,
  onCheck?: (item: T, value: boolean) => void
) => JSX.Element

export interface IHierarchyColumn<T = any> extends IColumn<T> {
  rowSpan?: number
  colspan?: number
  children?: IHierarchyColumn[]
  hierarchyName?: string
}

export interface IColumn<T = any> {
  /**
   * Displayed name of the column
   */
  title: string
  /**
   * Name of the field in source
   */
  field: string
  /**
   * Set up parent column key. Used for multiline header
   */
  parentField?: string
  /**
   * Visibility of the column
   */
  hidden?: boolean
  /**
   * The higher the number - the sooner the column will be hidden
   * when the widget width is reduced
   * (outdated because of the implementation of the horizontal scroll bar?)
   */
  priority?: number
  /**
   * Width of the column
   */
  width?: number | string
  /**
   * Visibility of settings button
   */
  hideSettings?: boolean
  /**
   * Visibility of sort icon
   */
  hideSortIcon?: boolean
  /**
   * Visibility of sort index number
   */
  hideSortIndex?: boolean
  /**
   * Visibility of the th filter icon when filters are active
   */
  hideFilterIcon?: boolean
  /**
   * Sorting availability
   */
  disableSort?: boolean
  /**
   * Custom template for column content
   */
  renderFunction?: (item: T, column: IColumn<T>, rowAttrs?: any) => JSX.Element | string
  /**
   * Custom template for column title
   */
  titleRender?: ({
    column,
    headerLine,
  }: {
    headerLine: number
    column: IHierarchyColumn
  }) => JSX.Element
  /**
   * Custom filters in settings
   */
  customFilter?: any
  /**
   * Disable operators in filters
   */
  withoutOperator?: boolean
  /**
   * Column title tooltip visibility. Default value: true
   */
  tooltipVisible?: boolean
  /**
   * If we want to make the column sticky, then we need to set its width
   */
  isStickyColumn?: boolean
  /**
   * Classname for td and th in column
   */
  className?: string
  /**
   * If true, the initial sort direction for column will be descending
   */
  sortDescFirst?: boolean
}

export interface IRowAction<T> {
  id: string
  title?: string
  icon?: string | ((item: T) => string)
  customView?: ({
    item,
    isDisabled,
    action,
  }: {
    item: T
    isDisabled: boolean
    action: () => void
  }) => JSX.Element
  action: (item: T) => void
  isVisible?: (item: T) => boolean
  isDisabled?: (item: T) => { isDisabled: boolean; reason?: string }
}

export enum TableMode {
  table = 'table',
  div = 'div',
}

export interface IRowsPerPageOption {
  key: number
  value: string
}

export type TTitleRenderTypes =
  | 'rowsCount'
  | 'thDisabled'
  | 'thEnabled'
  | 'thPopupDisabled'
  | 'thPopupEnabled'
  | 'thPopupFilterInput'
  | 'columnsFilter'

export type TTitleRenderFunction = (
  type: TTitleRenderTypes,
  text: string,
  data?: object
) => JSX.Element | false

interface IItemIterator {
  iterator: (data: { index: number; rowOptions?: ICustomRowOptions }) => JSX.Element
}

interface IColumnIterator {
  iterator: (data: { index: number; columnOptions?: ICustomColumnOptions }) => JSX.Element
}

interface IHeaderIterator {
  iterator: (data: { index: number; headerOptions?: ICustomHeaderOptions }) => JSX.Element
  level: number
}

export interface IBaseTableProps<T = IForm, R = string | number> {
  isSelect?: boolean // maybe remove?
  /**
   * Classes for inner elements
   */
  classes?: {
    root?: string
    header?: string
    loaderOverlay?: string
    body?: string
    scrollRoot?: string
  }
  styles?: {
    body?: CSSProperties
  }
  scrollRefObject?: RefObject<HTMLDivElement>
  itemIterator?: (data: IItemIterator) => JSX.Element
  columnIterator?: (data: IColumnIterator) => JSX.Element
  headerIterator?: (data: IHeaderIterator) => JSX.Element
  /**
   * Change display mode to the list of div,
   * allowing you to create customizable layouts like cards grid or tree
   */
  mode?: TableMode
  /**
   * Custom header template
   */
  headerTpl?: <P = IForm, R = string | number>(props: ITHeadProps<P, R>) => JSX.Element
  /**
   * Custom header row template
   */
  headerRowTpl?: ({
    headerLine,
    children,
    classes,
  }: {
    headerLine: number
    children: ReactNode
    classes: string
  }) => JSX.Element
  /**
   * Custom footer template
   */
  footerTpl?: <P = IForm, R = string | number>(props: ITableProps<P, R>) => JSX.Element
  convertIFormToP?: (items: T[]) => T[]
  /**
   * Custom row template
   */
  rowTpl?: TplFunction<T>
  /**
   * Custom cell template
   */
  cellTpl?: (
    item: T,
    column: IColumn<T>,
    cellAttrs: ICellAttrs,
    rowState: any,
    focusedId?: any,
    rowAttrs?: any
  ) => JSX.Element
  /**
   * Custom header cell template
   */
  headerCellTpl?: ({
    column,
    className,
    additionalStyles,
    defaultRender,
  }: {
    column: IHierarchyColumn<T>
    className: string
    additionalStyles?: CSSProperties
    defaultRender: () => JSX.Element
  }) => JSX.Element
  /**
   * Add action buttons to rows
   */
  rowActions?: IRowAction<T>[]
  /**
   * Set display variant for row actions
   */
  rowActionsVisible?: 'hover' | 'visible' | 'hidden'
  /**
   * Custom hierarchy cell template
   */
  hierarchyTpl?: (
    item: T,
    rowSate: RowState,
    hierarchyClick: (item: T) => void,
    hierarchyContent: (
      item: T,
      rowSate: RowState,
      hierarchyClick: (item: T, hierarchyField?: string) => void
    ) => JSX.Element,
    cellAttrs?: ICellAttrs
  ) => JSX.Element
  /**
   * Custom hierarchy content template
   */
  hierarchyContent?: (
    item: T,
    rowSate: RowState,
    hierarchyClick: (item: T, hierarchyField?: string) => void
  ) => JSX.Element
  /**
   * Set columns to display and their settings
   */
  columns: IColumn<T>[]
  /**
   * Callback function fires on row click
   */
  onClick?: (
    item: T,
    additional: { hierarchyClick: any; isExpanded: any; event: MouseEvent }
  ) => void
  /**
   * Callback function fires on row expand
   */
  onExpand?: (item: T) => Promise<void>
  /**
   * Callback function fires on row select
   */
  onSelect?: (ids: any[]) => void
  /**
   * Callback function fires on data load
   */
  onDataLoad?: (data: T[], total: number) => void
  onDataLoadError?: (error: Error) => void
  /**
   * Callback function fires on row moving (useDrag needed)
   */
  onMove?: (id: string, fromParentId: string, toParentId: string, position: number) => void
  /**
   * Add expandable rows if source has hierarchy
   */
  hasHierarchy?: boolean
  /**
   * Enable pagination
   */
  pagination?: boolean
  /**
   * Show header
   */
  header?: boolean
  /**
   * Make header sticky on scroll
   */
  isHeaderSticky?: boolean
  /**
   * List of rows to expand by default, if source has hierarchy
   */
  expanded?: any[]
  /**
   * Allows to rearrange items using drag-n-drop
   */
  useDrag?: boolean
  /**
   * Force source updating when sourceUpdateCount value changed
   */
  sourceUpdateCount?: number
  /**
   * Class on root element
   */
  className?: string
  /**
   * Allow you to set available options for pager dropdown if pagination is enabled
   */
  rowsPerPageOptions?: IRowsPerPageOption[]
  cacheKey?: string
  /**
   * Add button to export to XLSX
   */
  hasExport?: boolean
  /**
   * Set tooltip on export button
   */
  exportTooltip?: () => JSX.Element
  /**
   * Set filename to exported file
   */
  exportFileName?: string
  /**
   * Enable keyboard navigation
   */
  useHotKeys?: boolean
  /**
   * Set selected rows (works only with expanded)
   */
  selectedRows?: any[]
  /**
   * Class for selected row
   */
  selectedRowClassName?: string
  /**
   * Class for focused row
   */
  focusedRowClassName?: string
  localFiltersKey?: string
  /**
   * Add checkboxes to rows
   */
  useRowCheckbox?: boolean
  /**
   * Callback function fires on checkbox checked (useRowCheckbox needed)
   */
  onCheck?: (item: T, value: boolean, data: T[]) => void
  /**
   * List of checked rows
   */
  checkedValues?: ICheckedValue[]
  /**
   * Return true to disable the row checkbox (useRowCheckbox needed)
   */
  getRowCheckboxDisabled?: (item: T) => boolean
  /**
   * Add refresh button to footer
   */
  hasRefresh?: boolean
  /**
   * Add settings button with popup to footer, by default it's true
   */
  hasColumnsVisibilityOptions?: boolean
  columnsFilterKey?: string
  /**
   * Set tabindex to body of the table
   */
  tabIndex?: number
  /**
   * Height of the placeholder for table in rows
   */
  emptyPageSize?: number
  /**
   * Custom template for columns titles
   */
  titleRender?: TTitleRenderFunction
  /**
   * Set measures
   */
  measure?: {
    startLoadData: () => void
    stopLoadData: () => void
  }
  /**
   * Set columns to export
   */
  exportColumns?: IColumn[]
  /**
   * Custom placeholder content
   */
  placeholderContent?: () => ReactNode | string
  /**
   * Set filter to source
   */
  filter?: IDataProviderFilter
  /**
   * Callback function fires on export button click
   */
  onExportClick?: () => void

  multiSelect?: boolean // todo: remove
  flexWidth?: string // todo: remove
  disableKeyEvents?: boolean // todo: remove
}

export interface ITableProps<T = IForm, R = string | number> extends IBaseTableProps<T> {
  /**
   * Source for table
   */
  source: BaseCrud<T, R>
  /**
   * Alternative way to set data
   */
  items?: T[]
  /**
   * Set key field for source
   */
  keyField?: string
  /**
   * Number of rows to display on one page
   */
  pageSize?: number
}
export interface IStaticTableProps<T = IForm, R = string | number> extends IBaseTableProps<T> {
  /**
   * Source for table
   */
  source?: BaseCrud<T, R>
  /**
   * Alternative way to set data
   */
  items: T[]
  /**
   * Set key field for source
   */
  keyField: string
  /**
   * Number of rows to display on one page
   */
  pageSize?: number
}

export type TTableProps<P, R> = ITableProps<P, R> | IStaticTableProps<P, R>

export interface ICheckedValue {
  /**
   * Value of keyField for current row
   */
  key: string | number
  /**
   * Checkbox value
   */
  value: 'full' | 'empty' | 'indeterminate'
}

export interface IRowOptions {
  key: string
  /**
   * Use only for tests
   */
  testId?: string
  deep: number
  onClick: (e: MouseEvent) => void
  className: string
  index: number
}

export interface IBaseCustomOptions {
  className?: string
  style?: CSSProperties
}
export interface ICustomRowOptions extends IBaseCustomOptions {}

export interface ICustomColumnOptions extends IBaseCustomOptions {}

export interface ICustomHeaderOptions extends IBaseCustomOptions {}
